import { Plus, ChevronLeft, ChevronRight, AlertCircle } from "lucide-react";

export interface EntryRailItem {
  id: string;
  title: string;
  subtitle: string;
  incomplete: boolean;
}

interface EntryRailProps {
  items: EntryRailItem[];
  activeId: string | null;
  /** Hex accent for the active card / index badge (e.g. "var(--acc2)"). */
  accent: string;
  addLabel: string;
  onSelect: (id: string) => void;
  onAdd: () => void;
}

/**
 * Horizontal summary rail for a master–detail step: one compact card per entry
 * plus an "Add" tile. Pages left/right so the step never grows vertically with
 * the entry count (the editor for the selected entry renders below — see
 * ExperienceStep / EducationStep).
 */
export function EntryRail({
  items,
  activeId,
  accent,
  addLabel,
  onSelect,
  onAdd,
}: EntryRailProps) {
  const activeIndex = items.findIndex((i) => i.id === activeId);

  const go = (dir: -1 | 1) => {
    if (items.length < 2 || activeIndex < 0) return;
    const next = (activeIndex + dir + items.length) % items.length;
    onSelect(items[next].id);
  };

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold" style={{ color: "var(--landing-text-muted)" }}>
          {activeIndex >= 0 ? `${activeIndex + 1} of ${items.length}` : `${items.length} entries`}
        </span>
        {items.length > 1 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous entry"
              className="flex h-7 w-7 items-center justify-center rounded-lg border transition-colors hover:text-[var(--landing-text)]"
              style={{ borderColor: "var(--landing-glass-border)", color: "var(--landing-text-muted)" }}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next entry"
              className="flex h-7 w-7 items-center justify-center rounded-lg border transition-colors hover:text-[var(--landing-text)]"
              style={{ borderColor: "var(--landing-glass-border)", color: "var(--landing-text-muted)" }}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <div className="-mx-1 flex snap-x gap-2 overflow-x-auto px-1 pb-1">
        {items.map((it, i) => {
          const isActive = it.id === activeId;
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => onSelect(it.id)}
              aria-current={isActive}
              className="w-40 shrink-0 snap-start rounded-xl border p-3 text-left transition-colors"
              style={{
                background: isActive ? `${accent}14` : "var(--landing-surface)",
                borderColor: it.incomplete
                  ? "rgba(239,68,68,0.5)"
                  : isActive
                    ? accent
                    : "var(--landing-glass-border)",
              }}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold" style={{ color: accent }}>
                  {i + 1}
                </span>
                {it.incomplete && <AlertCircle className="h-3 w-3 text-destructive" />}
              </div>
              <p className="mt-1 truncate text-sm font-semibold" style={{ color: "var(--landing-text)" }}>
                {it.title || "Untitled"}
              </p>
              <p className="truncate text-xs" style={{ color: "var(--landing-text-muted)" }}>
                {it.subtitle || "—"}
              </p>
            </button>
          );
        })}

        <button
          type="button"
          onClick={onAdd}
          className="flex w-28 shrink-0 snap-start flex-col items-center justify-center gap-1 rounded-xl border border-dashed p-3 transition-colors hover:border-[color:var(--landing-text-muted)]"
          style={{ borderColor: "var(--landing-glass-border)", color: "var(--landing-text-secondary)" }}
        >
          <Plus className="h-4 w-4" />
          <span className="text-xs font-semibold">{addLabel}</span>
        </button>
      </div>
    </div>
  );
}
